import { Chip, Stack, Typography, useTheme } from '@mui/material';
import { ReactElement } from 'react';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { NormalizedCustomAttributeDefinition } from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { getCustomAttributeConfig } from 'src/graphql/entities/custom-attributes/field-view-registry';
import {
  getAttributeOptionLabel,
  getKindLikeValueSafely,
} from 'src/graphql/entities/custom-attributes/field-view-registry/utils';
import { useSelectedPartner } from 'src/graphql/entities/partner/partner';
import { useOverflowObserver } from 'src/utils/hooks/use-overflow-observer';

interface CustomAttributeCellProps {
  value: unknown;
  definition: NormalizedCustomAttributeDefinition;
}

const MultiSelectChipsCell = ({ labels }: { labels: string[] }): ReactElement | null => {
  const theme = useTheme();
  const { nodeRef, isOverflowing } = useOverflowObserver();

  if (!labels.length) {
    return null;
  }

  const chips = labels.map((label) => (
    <Chip
      key={label}
      size="small"
      label={label}
      sx={{ height: 'auto', bgcolor: 'grey.200', color: 'text.primary' }}
    />
  ));

  return (
    <Tooltip
      title={
        isOverflowing ? (
          <Stack useFlexGap gap={1} direction="row" flexWrap="wrap">
            {chips}
          </Stack>
        ) : null
      }
      placement="bottom-start"
      slotProps={{ tooltip: { sx: { padding: theme.spacing(1) } } }}
    >
      <Stack useFlexGap gap={1} direction="row" ref={nodeRef}>
        {chips}
      </Stack>
    </Tooltip>
  );
};

const TruncatedTextCell = ({ text }: { text: string }): ReactElement => (
  <Tooltip title={text} enterDelay={1000}>
    <Typography
      variant="body2"
      component="span"
      sx={{
        display: 'block',
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {text}
    </Typography>
  </Tooltip>
);

export const CustomAttributeCell = ({
  value,
  definition,
}: CustomAttributeCellProps): ReactElement | null => {
  const config = getCustomAttributeConfig(definition.type);
  const { selectedPartner } = useSelectedPartner();
  const timezone = selectedPartner?.timeZone || undefined;

  // single-row chips with an overflow tooltip (the Labels-column pattern); the panel's View wraps.
  if (definition.type === 'multi_select') {
    const keys = getKindLikeValueSafely(config.decode(value), 'multi_select') ?? [];
    const labels = keys.map((key) => getAttributeOptionLabel(definition.options, key));

    return <MultiSelectChipsCell labels={labels} />;
  }

  const decoded = config.decode(value);

  if (config.isEmpty(decoded)) {
    return null;
  }

  if (definition.type === 'short_text' || definition.type === 'multi_line_text') {
    return <TruncatedTextCell text={getKindLikeValueSafely(decoded, definition.type) ?? ''} />;
  }

  return <config.View value={decoded} definition={definition} timezone={timezone} />;
};
